/* bits and bytes of the scrollTo function inspired by the works of Benjamin DeCock */

import { MoveableDirection } from './Swimlane';

/*
 * Easing Functions - inspired from http://gizma.com/easing/
 * only considering the t value for the range [0, 1] => [0, 1]
 */
const easingFunctions = {
  // no easing, no acceleration
  linear: (t: number) => {
    return t;
  },
  // accelerating from zero velocity
  easeInQuad: (t: number) => {
    return t * t;
  },
  // decelerating to zero velocity
  easeOutQuad: (t: number) => {
    return t * (2 - t);
  },
  // acceleration until halfway, then deceleration
  easeInOutQuad: (t: number) => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  },
  // accelerating from zero velocity
  easeInCubic: (t: number) => {
    return t * t * t;
  },
  // decelerating to zero velocity
  easeOutCubic: (t: number) => {
    return --t * t * t + 1;
  },
  // acceleration until halfway, then deceleration
  easeInOutCubic: (t: number) => {
    return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
  },
  // accelerating from zero velocity
  easeInQuart: (t: number) => {
    return t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuart: (t: number) => {
    return 1 - --t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuart: (t: number) => {
    return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
  },
  // accelerating from zero velocity
  easeInQuint: (t: number) => {
    return t * t * t * t * t;
  },
  // decelerating to zero velocity
  easeOutQuint: (t: number) => {
    return 1 + --t * t * t * t * t;
  },
  // acceleration until halfway, then deceleration
  easeInOutQuint: (t: number) => {
    return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
  },
};

interface BaseScrollOptions {
  target: HTMLElement;
  duration: number;
  easing: keyof typeof easingFunctions;
  callback?: (target: HTMLElement) => void;
}

interface ScrollToOptions extends BaseScrollOptions {
  to: number;
}

interface ScrollByOptions extends BaseScrollOptions {
  by: number;
}

type ScrollLeftOptions = ScrollToOptions | ScrollByOptions;

export function scroll(options: ScrollLeftOptions) {
  const { target, duration, easing, callback } = options;

  const x = 'to' in options ? options.to : target.scrollLeft - options.by;

  const start = Date.now();
  const from = target.scrollLeft;

  if (from === x) {
    callback?.(target);
    return; /* Prevent scrolling to the X point if already there */
  }

  function _scroll() {
    const currentTime = Date.now();
    const time = Math.min(1, (currentTime - start) / duration);
    const easedT = easingFunctions[easing](time);

    target.scrollLeft = easedT * (x - from) + from;

    if (time < 1) {
      requestAnimationFrame(_scroll);
    } else if (callback) {
      callback(target);
    }
  }

  requestAnimationFrame(_scroll);
}

export function getMovableDirection({ scrollWidth, clientWidth, scrollLeft }: HTMLElement): MoveableDirection {
  const max = scrollWidth - clientWidth;

  if (max === 0) {
    return 'none';
  }

  if (scrollLeft === max) {
    return 'start';
  }

  if (scrollLeft === 0) {
    return 'end';
  }

  return 'both';
}
